// Theme context - FIXED VERSION
import React, { createContext, useState, useEffect, useLayoutEffect, useRef } from 'react';
import storageService from '../services_main/storageService';

export const ThemeContext = createContext();

function getInitialDarkMode() {
  if (typeof window === 'undefined') return false;

  let savedTheme = null;
  try {
    savedTheme = storageService.getItem('darkMode');
  } catch {
    // Storage can be unavailable in private browsing or restricted embeds.
  }
  if (savedTheme !== null) {
    try {
      return Boolean(JSON.parse(savedTheme));
    } catch {
      // The persistence effect below replaces invalid stored values.
    }
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches || false;
}

export function ThemeProvider({ children }) {
  // Resolve the saved/system preference during the first render so the app
  // does not paint in light mode and immediately repaint in dark mode.
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);
  const hasAppliedInitialTheme = useRef(false);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const isInitialApplication = !hasAppliedInitialTheme.current;
    let firstFrame;
    let secondFrame;

    if (isInitialApplication) root.classList.add('theme-initializing');

    body.classList.toggle('dark-mode', darkMode);
    body.classList.toggle('light-mode', !darkMode);
    root.classList.toggle('dark-mode', darkMode);
    root.classList.toggle('light-mode', !darkMode);
    hasAppliedInitialTheme.current = true;

    // Keep transitions disabled through the first painted frame, then restore
    // them for deliberate visitor-triggered theme changes.
    if (isInitialApplication) {
      firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(() => {
          root.classList.remove('theme-initializing');
        });
      });
    }

    return () => {
      if (firstFrame !== undefined) window.cancelAnimationFrame(firstFrame);
      if (secondFrame !== undefined) window.cancelAnimationFrame(secondFrame);
      root.classList.remove('theme-initializing');
    };
  }, [darkMode]);
  
  // Persist theme changes separately from the pre-paint DOM update.
  useEffect(() => {
    try {
      storageService.setItem('darkMode', JSON.stringify(darkMode));
    } catch {
      // Storage can be unavailable in private browsing or restricted embeds.
    }
  }, [darkMode]);
  
  // Toggle theme
  const toggleTheme = () => {
    setDarkMode(prevMode => !prevMode);
  };
  
  return (
    <ThemeContext.Provider value={{ darkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
